import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import {
  isPerBookingBilling,
  isPerBookingSignupLocked,
  needsPerBookingSignupFee,
  perBookingGraceDaysRemaining,
  perBookingGraceEndsAt,
} from "@/lib/billing-model";

interface TrialStatus {
  isTrial: boolean;
  isLocked: boolean;
  isInGrace: boolean;
  daysRemaining: number; // days remaining until next cutoff (trial end, or grace end)
  trialEndsAt: string | null;
  graceEndsAt: string | null;
  trialStartedAt: string | null;
}

export function useTrial() {
  const { business, loading: businessLoading } = useBusiness();
  const [trialStatus, setTrialStatus] = useState<TrialStatus>({
    isTrial: false,
    isLocked: false,
    isInGrace: false,
    daysRemaining: 0,
    trialEndsAt: null,
    graceEndsAt: null,
    trialStartedAt: null,
  });
  const [loading, setLoading] = useState(true);

  // Fetch trial status from DB (server-time) so UI matches production truth
  const fetchTrialStatus = useCallback(async () => {
    if (!business) {
      setTrialStatus({
        isTrial: false,
        isLocked: false,
        isInGrace: false,
        daysRemaining: 0,
        trialEndsAt: null,
        graceEndsAt: null,
        trialStartedAt: null,
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      if (isPerBookingBilling(business)) {
        const locked = isPerBookingSignupLocked(business);
        const inSignupGrace = needsPerBookingSignupFee(business) && !locked;
        setTrialStatus({
          isTrial: inSignupGrace,
          isLocked: locked,
          isInGrace: false,
          daysRemaining: perBookingGraceDaysRemaining(business),
          trialEndsAt: perBookingGraceEndsAt(business),
          graceEndsAt: null,
          trialStartedAt: ((business as any)?.trial_started_at as string | null) || null,
        });
        return;
      }

      const subscriptionStatus = (business as any)?.subscription_status as string | undefined;
      const trialEndsAt = (business as any)?.trial_ends_at as string | null | undefined;
      const trialStartedAt = (business as any)?.trial_started_at as string | null | undefined;

      const isTrial = subscriptionStatus === "trial";

      // If not on trial, don't show any countdown
      if (!isTrial) {
        setTrialStatus({
          isTrial: false,
          isLocked: false,
          isInGrace: false,
          daysRemaining: 0,
          trialEndsAt: trialEndsAt || null,
          graceEndsAt: null,
          trialStartedAt: trialStartedAt || null,
        });
        return;
      }

      // Pull computed values from DB (server time), so it matches your SQL
      const [
        { data: daysData, error: daysErr },
        { data: trialEndedData, error: endedErr },
        { data: lockedData, error: lockedErr },
        { data: graceDaysData, error: graceDaysErr },
        { data: graceEndsData, error: graceEndsErr },
      ] =
        await Promise.all([
          supabase.rpc("get_trial_days_remaining", { _business_id: business.id }),
          supabase.rpc("is_trial_expired", { _business_id: business.id }), // trial ended (not lock)
          supabase.rpc("is_trial_locked", { _business_id: business.id }),
          supabase.rpc("get_trial_grace_days_remaining", { _business_id: business.id }),
          supabase.rpc("get_trial_grace_ends_at", { _business_id: business.id }),
        ]);

      if (daysErr) throw daysErr;
      if (endedErr) throw endedErr;
      if (lockedErr) throw lockedErr;
      if (graceDaysErr) throw graceDaysErr;
      if (graceEndsErr) throw graceEndsErr;

      const trialDaysRemaining = Number(daysData ?? 0);
      const trialEnded = Boolean(trialEndedData);
      const isLocked = Boolean(lockedData);
      const graceDaysRemaining = Number(graceDaysData ?? 0);
      const isInGrace = trialEnded && !isLocked;

      const daysRemaining = isInGrace ? graceDaysRemaining : trialDaysRemaining;

      setTrialStatus({
        isTrial: true,
        isLocked,
        isInGrace,
        daysRemaining: Math.max(0, Number.isFinite(daysRemaining) ? daysRemaining : 0),
        trialEndsAt: trialEndsAt || null,
        graceEndsAt: (graceEndsData as any) ? String(graceEndsData) : null,
        trialStartedAt: trialStartedAt || null,
      });
    } catch (error) {
      console.error("Error fetching trial status:", error);
    } finally {
      setLoading(false);
    }
  }, [business]);

  // Initial calculation when business loads
  useEffect(() => {
    fetchTrialStatus();
  }, [fetchTrialStatus]);

  // Keep it in sync while user is in-app (DB-time refresh)
  useEffect(() => {
    if (!business) return;

    const subscriptionStatus = (business as any)?.subscription_status;
    const onSubscriptionTrial = subscriptionStatus === "trial";
    const onPerBookingGrace =
      isPerBookingBilling(business) && needsPerBookingSignupFee(business) && !isPerBookingSignupLocked(business);

    if (!onSubscriptionTrial && !onPerBookingGrace) {
      return;
    }

    fetchTrialStatus();

    const interval = setInterval(() => {
      fetchTrialStatus();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [business, fetchTrialStatus]);

  return {
    ...trialStatus,
    loading: loading || businessLoading,
    refetch: fetchTrialStatus,
  };
}
